import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import ExportBar from "../../Components/Accounting/ExportBar";
import Select from "react-select";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";

const BudgetVsActual = () => {
  const { id }   = useParams();        // optional budget id
  const navigate = useNavigate();

  const [budgets,    setBudgets]    = useState([]);
  const [fyList,     setFyList]     = useState([]);
  const [selectedFY, setSelectedFY] = useState(null);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [vaData,     setVaData]     = useState(null);
  const [loading,    setLoading]    = useState(false);

  useEffect(() => { fetchMeta(); }, []);
  useEffect(() => { if (selectedFY) fetchBudgets(); }, [selectedFY]);
  useEffect(() => { if (id && budgets.length) {
    const found = budgets.find((b) => String(b.id) === String(id));
    if (found) setSelectedBudget({ value: found.id, label: found.name });
  }}, [id, budgets]);
  useEffect(() => { if (selectedBudget) fetchVA(); }, [selectedBudget]);

  const fetchMeta = async () => {
    const res = await ajaxAccounting.listFinancialYears({});
    if (res.status === "OK") {
      const list = res.details || [];
      setFyList(list);
      const cur = list.find((y) => y.is_current === 1) || list[0];
      if (cur) setSelectedFY({ value: cur.id, label: cur.name });
    }
  };

  const fetchBudgets = async () => {
    const res = await ajaxAccounting.listBudgets({ financial_year_id: selectedFY?.value });
    if (res.status === "OK") {
      const list = Array.isArray(res.details) ? res.details : res.details?.list || [];
      setBudgets(list);
      if (list.length && !selectedBudget) setSelectedBudget({ value: list[0].id, label: list[0].name });
    }
  };

  const fetchVA = async () => {
    if (!selectedBudget) return;
    setLoading(true);
    setVaData(null);
    const res = await ajaxAccounting.getBudgetVsActual({
      budget_id:         selectedBudget.value,
      financial_year_id: selectedFY?.value,
    });
    if (res.status === "OK") setVaData(res.details);
    else toast.error(res.message || "Failed to load budget vs actual");
    setLoading(false);
  };

  const fyOptions     = fyList.map((y) => ({ value: y.id, label: y.name }));
  const budgetOptions = budgets.map((b) => ({ value: b.id, label: b.name }));

  const lines        = vaData?.lines        || [];
  const totalBudget  = vaData?.total_budget || lines.reduce((s, l) => s + (parseFloat(l.budget_amount) || 0), 0);
  const totalActual  = vaData?.total_actual || lines.reduce((s, l) => s + (parseFloat(l.actual_amount) || 0), 0);
  const totalVar     = totalBudget - totalActual;
  const pctUsed      = totalBudget > 0 ? ((totalActual / totalBudget) * 100) : 0;

  const exportCols = [
    { key:"account_code",  label:"Code" },
    { key:"account_name",  label:"Account" },
    { key:"budget_amount", label:"Budget" },
    { key:"actual_amount", label:"Actual" },
    { key:"variance",      label:"Variance" },
    { key:"pct_used",      label:"% Used" },
  ];

  const exportData = lines.map((l) => ({
    ...l,
    variance: (l.budget_amount || 0) - (l.actual_amount || 0),
    pct_used: l.budget_amount > 0 ? `${((l.actual_amount / l.budget_amount) * 100).toFixed(1)}%` : "N/A",
  }));

  return (
    <AppContainer
      title="Budget vs Actual"
      previousPageTitle="Budget"
      description="Compare budgeted amounts against actual expenditure by account. Red variance means over-budget, green means under-budget. Use this to identify areas needing attention."
      actions={[
        { label:"Back to Budgets", icon:"fa-solid fa-arrow-left", className:"btn btn-light", onClick:() => navigate("/accounting/budget") },
      ]}
    >
      <Toaster />

      {/* Selectors */}
      <div className="card custom-card p-3 mb-3">
        <div className="row g-2">
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Financial Year</label>
            <Select options={fyOptions} value={selectedFY} onChange={setSelectedFY} placeholder="Select year" isSearchable />
          </div>
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Budget</label>
            <Select options={budgetOptions} value={selectedBudget} onChange={setSelectedBudget}
              placeholder="Select budget" isSearchable
              noOptionsMessage={() => "No budgets — create one first"} />
          </div>
        </div>
      </div>

      {/* KPI summary row */}
      {vaData && (
        <div className="row g-2 mb-3">
          {[
            { label:"Total Budget",   value: totalBudget, color:"#6366f1" },
            { label:"Total Actual",   value: totalActual, color:"#ef4444" },
            { label:"Variance",       value: Math.abs(totalVar), color: totalVar >= 0 ? "#10b981" : "#ef4444",
              suffix: totalVar >= 0 ? " ↑ Under budget" : " ↓ Over budget" },
            { label:"% Used",         value: null,        color: pctUsed > 90 ? "#ef4444" : pctUsed > 75 ? "#f59e0b" : "#10b981",
              custom: `${pctUsed.toFixed(1)}%` },
          ].map((c, i) => (
            <div key={i} className="col-xl-3 col-sm-6">
              <div className="card custom-card p-3" style={{ borderLeft:`3px solid ${c.color}` }}>
                <div className="tx-11 tx-muted">{c.label}</div>
                <div className="fw-bold tx-16" style={{ color:c.color }}>
                  {c.custom || functions.formatCurrency(c.value)}
                  {c.suffix && <span className="tx-11 ms-1">{c.suffix}</span>}
                </div>
                {i === 3 && (
                  <div style={{ height:"6px", background:"#e5e7eb", borderRadius:"3px", marginTop:"6px" }}>
                    <div style={{ width:`${Math.min(pctUsed,100)}%`, height:"100%", background:c.color, borderRadius:"3px", transition:"width 0.5s" }}></div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Main table */}
      <div className="card custom-card p-3">
        {loading && (
          <div className="text-center py-5">
            <i className="fa-solid fa-spinner fa-spin fa-2x tx-primary"></i>
            <p className="mt-2 tx-muted">Loading budget vs actual...</p>
          </div>
        )}

        {!loading && !vaData && (
          <div className="text-center py-5 tx-muted">
            <i className="fa-solid fa-chart-bar" style={{ fontSize:"3rem", opacity:0.3 }}></i>
            <p className="mt-2">Select a financial year and budget to view the comparison.</p>
          </div>
        )}

        {!loading && vaData && (
          <>
            <ExportBar data={exportData} columns={exportCols}
              filename={`bva-${selectedBudget?.label}`} pdfTitle={`Budget vs Actual — ${selectedBudget?.label}`}
              printCode="ACC-BUDGET" exportCode="ACC-BUDGET" />

            <div className="table-responsive mt-2">
              <table className="table table-bordered table-sm">
                <thead>
                  <tr className="bg-light">
                    <th style={{ minWidth:"60px" }}>Code</th>
                    <th>Account</th>
                    <th className="text-end">Budget</th>
                    <th className="text-end">Actual</th>
                    <th className="text-end">Variance</th>
                    <th style={{ width:"140px" }}>% Used</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-4 tx-muted">No budget lines found. Add budget lines first.</td></tr>
                  )}
                  {lines.map((line, i) => {
                    const variance  = (parseFloat(line.budget_amount) || 0) - (parseFloat(line.actual_amount) || 0);
                    const pct       = line.budget_amount > 0 ? (line.actual_amount / line.budget_amount) * 100 : 0;
                    const overBudget = variance < 0;
                    return (
                      <tr key={i}>
                        <td><code className="tx-11">{line.account_code}</code></td>
                        <td>{line.account_name}</td>
                        <td className="text-end">{functions.formatCurrency(line.budget_amount)}</td>
                        <td className="text-end">{functions.formatCurrency(line.actual_amount)}</td>
                        <td className={`text-end fw-semibold ${overBudget ? "tx-danger" : "tx-success"}`}>
                          {overBudget ? "−" : "+"}{functions.formatCurrency(Math.abs(variance))}
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <span className="tx-12" style={{ minWidth:"38px" }}>{pct.toFixed(0)}%</span>
                            <div style={{ flex:1, height:"8px", background:"#e5e7eb", borderRadius:"4px", overflow:"hidden" }}>
                              <div style={{
                                width:`${Math.min(pct,100)}%`, height:"100%",
                                background: pct > 100 ? "#ef4444" : pct > 85 ? "#f59e0b" : "#10b981",
                                borderRadius:"4px", transition:"width 0.4s",
                              }}></div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-light fw-semibold">
                  <tr>
                    <td colSpan={2}>Totals</td>
                    <td className="text-end">{functions.formatCurrency(totalBudget)}</td>
                    <td className="text-end">{functions.formatCurrency(totalActual)}</td>
                    <td className={`text-end ${totalVar >= 0 ? "tx-success" : "tx-danger"}`}>
                      {totalVar >= 0 ? "+" : "−"}{functions.formatCurrency(Math.abs(totalVar))}
                    </td>
                    <td>{pctUsed.toFixed(1)}%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>
    </AppContainer>
  );
};

export default BudgetVsActual;
